import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * Theme system (Spec 7.1). A theme is a set of semantic-token overrides
 * applied via <html data-theme="…"> (see styles/themes.css). This context
 * owns the active theme, persists it to localStorage, and keeps the <html>
 * attribute in sync. v1 ships two dark themes; light themes drop in by
 * adding to THEMES + a block in themes.css.
 */

export type ThemeId = 'midnight' | 'obsidian';

export interface ThemeOption {
  id: ThemeId;
  label: string;
  swatch: string;            // representative bg color for the picker dot
  kind: 'dark' | 'light';
}

export const THEMES: ThemeOption[] = [
  { id: 'midnight', label: 'Midnight', swatch: '#090d18', kind: 'dark' },
  { id: 'obsidian', label: 'Obsidian', swatch: '#070809', kind: 'dark' },
];

const STORAGE_KEY = 'quantify-theme';
const DEFAULT_THEME: ThemeId = 'midnight';
const VALID_THEMES = new Set<string>(THEMES.map((t) => t.id));

function readStoredTheme(): ThemeId {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && VALID_THEMES.has(stored)) return stored as ThemeId;
  } catch {
    // localStorage unavailable (private mode / disabled) — use the default.
  }
  return DEFAULT_THEME;
}

function applyThemeAttribute(id: ThemeId): void {
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = id;
  }
}

interface ThemeContextValue {
  theme: ThemeId;
  setTheme: (id: ThemeId) => void;
  themes: ThemeOption[];
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeId>(() =>
    typeof window === 'undefined' ? DEFAULT_THEME : readStoredTheme()
  );

  // Keep the <html data-theme> attribute in sync (also covers the initial mount).
  useEffect(() => {
    applyThemeAttribute(theme);
  }, [theme]);

  const setTheme = useCallback((id: ThemeId) => {
    const next: ThemeId = VALID_THEMES.has(id) ? id : DEFAULT_THEME;
    setThemeState(next);
    applyThemeAttribute(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Persisting failed — the theme still applies for this session.
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
